import { useState, useEffect, useCallback } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AuthenticateWithRedirectCallback, useAuth } from "@clerk/tanstack-react-start";
import { toast } from "sonner";
import { OnboardingLayout, OnboardingCard } from "@/components/onboarding-layout";
import { Spinner } from "@/components/ui/spinner";
import { getOnboardingStatusServer } from "@/server/onboarding";
import { acceptInvitationAndJoinServer } from "@/server/workspace-invitations";
import { logInfo, logError } from "@/lib/structured-log";
import { extractApiErrorMessage } from "@/lib/api/error";

// localStorage key for persisting invitation token through SSO flow
const PENDING_INVITATION_KEY = "pending_workspace_invitation";

export const Route = createFileRoute("/signin/sso-callback")({
  component: SSOCallbackPage,
});

function SSOCallbackPage() {
  const navigate = useNavigate();
  const { isLoaded: isAuthLoaded, isSignedIn } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState<"authenticating" | "joining" | "completing">(
    "authenticating",
  );

  // Handle accepting invitation and joining workspace
  const handleAcceptInvitation = useCallback(
    async (token: string): Promise<boolean> => {
      setStatus("joining");
      try {
        logInfo("auth.sso.invitation.accepting", { token_prefix: token.slice(0, 8) });
        const result = await acceptInvitationAndJoinServer({ data: { token } });

        // Clear the pending invitation from localStorage
        try {
          localStorage.removeItem(PENDING_INVITATION_KEY);
        } catch {
          // localStorage might be disabled, ignore
        }

        logInfo("auth.sso.invitation.accepted", {
          workspace_id: result.workspace_id,
          workspace_slug: result.workspace_slug,
        });

        navigate({
          to: "/workspace/$workspaceSlug/new-task",
          params: { workspaceSlug: result.workspace_slug ?? "" },
        });
        return true;
      } catch (error) {
        // Clear invalid token from localStorage
        try {
          localStorage.removeItem(PENDING_INVITATION_KEY);
        } catch {
          // localStorage might be disabled, ignore
        }

        const errorMessage = extractApiErrorMessage(error, "Failed to accept invitation");

        logError("auth.sso.invitation.failed", {
          error: errorMessage,
          token_prefix: token.slice(0, 8),
        });

        // Show user-friendly error message
        toast.error(errorMessage);
        return false;
      }
    },
    [navigate],
  );

  // Handle post-authentication flow
  useEffect(() => {
    if (!isAuthLoaded || !isSignedIn || isProcessing) return;

    // Retry mechanism for 401 errors
    const retryOperation = async (operation: () => Promise<void>, maxRetries = 3, delay = 1000) => {
      for (let i = 0; i < maxRetries; i++) {
        try {
          await operation();
          return;
        } catch (error) {
          const is401 = error instanceof Error && error.message.includes("401");
          if (is401 && i < maxRetries - 1) {
            logInfo(`auth.sso.retry_${i + 1}`, { delay });
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }
          throw error;
        }
      }
    };

    const handlePostAuth = async () => {
      setIsProcessing(true);

      try {
        // Ensure user exists in backend with retry logic
        await retryOperation(async () => {
          await getOnboardingStatusServer();
        });
        logInfo("auth.sso.complete", {});

        // Check for pending invitation token
        let invitationToken: string | null = null;
        try {
          invitationToken = localStorage.getItem(PENDING_INVITATION_KEY);
        } catch {
          // localStorage might be disabled, ignore
        }

        if (invitationToken) {
          logInfo("auth.sso.pending_invitation_found", {
            token_prefix: invitationToken.slice(0, 8),
          });

          const success = await handleAcceptInvitation(invitationToken);
          if (!success) {
            // If invitation failed, fallback to onboarding
            setStatus("completing");
            // Use window.location to ensure fresh cookie state, with a small delay
            setTimeout(() => window.location.replace("/onboarding"), 500);
          }
        } else {
          // No invitation, navigate to onboarding
          setStatus("completing");
          // Use window.location to ensure fresh cookie state, with a small delay
          setTimeout(() => window.location.replace("/onboarding"), 500);
        }
      } catch (error) {
        logError("auth.sso.ensure_user.failed", {
          error: error instanceof Error ? error.message : "unknown",
        });
        // Even if ensure_user fails, try to continue
        // The error boundary will handle persistent auth issues
        setStatus("completing");
        // Use window.location to ensure fresh cookie state, with a small delay
        setTimeout(() => window.location.replace("/onboarding"), 500);
      }
    };

    void handlePostAuth();
  }, [isAuthLoaded, isSignedIn, isProcessing, navigate, handleAcceptInvitation]);

  const getStatusMessage = () => {
    switch (status) {
      case "joining":
        return "Joining workspace...";
      case "completing":
        return "Completing setup...";
      default:
        return "Setting up your account...";
    }
  };

  return (
    <OnboardingLayout title="Workus AI">
      <OnboardingCard className="text-center py-12">
        <div className="space-y-4">
          <Spinner className="w-6 h-6 mx-auto" />
          <p className="text-[20px] text-[#666666]">{getStatusMessage()}</p>
        </div>
        {/* AuthenticateWithRedirectCallback handles the OAuth callback */}
        {/* We use manual navigation in useEffect to handle invitation logic */}
        <AuthenticateWithRedirectCallback />
      </OnboardingCard>
    </OnboardingLayout>
  );
}
